/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import "./Issues.scss";

import React, { FunctionComponent, useEffect, useState } from "react";
import { Select, ButtonGroup, Input, Button } from "@itwin/itwinui-react";
import { Dialog } from "@itwin/core-react";
import { Textarea } from "@itwin/itwinui-react";
import { ModalDialogManager, ModelessDialogManager } from "@itwin/appui-react";

import { Point3d } from "@itwin/core-geometry";

import { cIssue, IssueData } from "./Issues";
import { cMessage } from "./Message";
import { cDialog } from "./Dialogs";

const issuesUpdateEvent = "issues.update-dialog";

const IssueDlg: FunctionComponent<{ isNew: boolean }> = ({ isNew }) => {
  const [dlgIssue, setDlgIssue] = useState(new IssueData());

  useEffect(() => {
    getNewIssue();

    cMessage.setupUiEvent(issuesUpdateEvent, getNewIssue);
    return () => {
      cMessage.removeUiEvent(issuesUpdateEvent);
    };
  }, []);

  const handleDialogClose = () => {
    cIssueDlg.closeDialog();
  };

  const [changeType, setChangeType] = useState<string>("Comment");
  const [changePriority, setChangePriority] = useState<string>("High");
  const [changeStatus, setChangeStatus] = useState<string>("Open");

  const onChangeType = (event: string) => {
    dlgIssue.type = event;
  };

  const onChangePriority = (event: string) => {
    dlgIssue.priority = event;
  };

  const onChangeStatus = (event: string) => {
    dlgIssue.status = event;
  };
  
  const onChangeTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    dlgIssue.title = event.target.value;
  };

  const onChangeDesc = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    dlgIssue.description = event.target.value;
  };

  const onChangeAssign = (event: React.ChangeEvent<HTMLInputElement>) => {
    dlgIssue.assignedTo = event.target.value;
  };

  const onSaveIssue = () => {
    handleDialogClose();
    // add new issue
    if (isNew) {
      cIssue.addNewIssue(dlgIssue);
    } else {
      // update existing issue
      cIssue.updateIssue(dlgIssue);
    }
  };

  const onOkDelete = () => {
    cDialog.closeConfirm();
    handleDialogClose();
    cIssue.deleteIssue(dlgIssue.id);
  };

  const onCancelDelete = () => {
    cDialog.closeConfirm();
  };

  const onDeleteIssue = () => {
    cDialog.confirm("Please confirm...", onOkDelete, onCancelDelete);
  };

  const onCancelIssue = () => {
    handleDialogClose();
  };

  const getNewIssue = () => {
    setDlgIssue(cIssueDlg.getDlgIssue());
  };

  // !!! AH
  // Changed the Dialog box according to 3.x
  // Only need to change Select

  /* 
    EXAMPLE:
    <Select<string>
          value={value.name}
          options={[
            { value: "Comment", label: "Comment" },
            { value: "Defect", label: "Defect" },
            { value: "Follow-up", label: "Follow-up" },
            { value: "Maintenance", label: "Maintenance" },
            { value: "Other", label: "Other" },
          ]}
          onChange={(option) => {
            onChangeType(option)
            setChangeType(option);
            
          }}
  */
  return (
    <Dialog
      opened={true}
      modal={true}
      onClose={handleDialogClose}
      onEscape={handleDialogClose}
      resizable={false}
      movable={true}
      width={350}
      title={
        <div>
          <span className="icon icon-issue-resolution"> </span>
          <b>{isNew ? "Add Event" : `Event: ${dlgIssue.id.toString()}`}</b>
        </div>
      }
    >
      <div id="_issueDlg" className="issues-4col">
        <span>Type: </span>
        <Select<string>
          value={dlgIssue.type}
          options={[
            { value: "Comment", label: "Comment" },
            { value: "Defect", label: "Defect" },
            { value: "Follow-up", label: "Follow-up" },
            { value: "Maintenance", label: "Maintenance" },
            { value: "Other", label: "Other" },
          ]}
          onChange={(option) => {
            onChangeType(option) // For changes in DB
            setChangeType(option); // For dynamic changes in the UI
            //dlgIssue.type = option;
          }}
          size="small"
          style={{ width: "120px" }}
          menuStyle={{ position: "absolute" }}
        />

        <span>Priority: </span>
        <Select<string>
          value={dlgIssue.priority}
          options={[
            { value: "High", label: "High" },
            { value: "Normal", label: "Normal" },
            { value: "Low", label: "Low" },
          ]}
          onChange={(option) => {
            onChangePriority(option);
            setChangePriority(option);
          }}
          size="small"
          style={{ width: "100px" }}
          menuStyle={{ position: "absolute"}}
        />
      </div>

      <span>Title: </span>
      <Input
        type="text"
        defaultValue={dlgIssue.title}
        onChange={onChangeTitle}
        style={{ bottom: "20px" }}
        size="small"
      ></Input>

      <span>Description: </span>
      <Textarea
        defaultValue={dlgIssue.description}
        onChange={onChangeDesc}
        cols={50}
        style={{ overflow: "scroll", height: "5rem" }}
      />

      <span>Assigned To: </span>
      <Input
        type="text"
        defaultValue={dlgIssue.assignedTo}
        onChange={onChangeAssign}
        size="small"
      ></Input>

      <div className="issues-2col">
        <span>Status:</span>
        <Select<string>
          value={dlgIssue.status}
          options={[
            { value: "Open", label: "Open" },
            { value: "Active", label: "Active" },
            { value: "On Hold", label: "On Hold" },
            { value: "Closed", label: "Closed" },
          ]}
          onChange={(option) => {
            onChangeStatus(option);
            setChangeStatus(option);
          }}
          size="small"
          style={{ width: "100px", position: "relative", right: "120px" }}
          menuStyle={{ position: "absolute"}}
        />
      </div>
      <div className="issues-dlg-footer">
        <Button onClick={onSaveIssue} size="small" styleType="high-visibility">
          <span className="icon icon-save"> </span>
          Save
        </Button>
        {!isNew ? (
          <Button
            onClick={onDeleteIssue}
            size="small"
            styleType="high-visibility"
          >
            <span className="icon icon-delete"> </span>
            Delete
          </Button>
        ) : (
          <span />
        )}

        <Button
          onClick={onCancelIssue}
          size="small"
          styleType="high-visibility"
        >
          <span className="icon icon-cancel"> </span>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
};

//===================================================================
// cIssueDlg
//===================================================================
export class cIssueDlg {
  private static _dlgIssue: IssueData;
  //  public static currIssueLocation : Point3d;

  public static newIssue(point: Point3d) {
    if (this.isDialogOpen()) {
      cMessage.showInfo("Please close current dialog before creating a new Event");
      return;
    }

    this._dlgIssue = new IssueData();
    this._dlgIssue.setLocation(point);

    this._dlgIssue.id = 0; //!!OA  cIssue.nextIssueId(false);
    this._dlgIssue.type = "Comment";
    this._dlgIssue.status = "Open";
    this._dlgIssue.priority = "High";

    this.openDialog(true);
  }

  public static updateIssue = (issue: IssueData) => {
    this._dlgIssue = issue;

    if (this.isDialogOpen()) {
      cMessage.raiseUiEvent(issuesUpdateEvent);
    } else {
      this.openDialog(false);
    }
  }

  public static getDlgIssue = (): IssueData => {
    return this._dlgIssue;
  }

  public static isDialogOpen = (): boolean => {
    if (ModelessDialogManager.getDialogInfo("issueDialog")) {
      return true;
    } else {
      return false;
    }
  }

  public static openDialog = (isNew: boolean) => {
    ModelessDialogManager.openDialog(this.issuesDialog(isNew), "issueDialog");
  }

  private static issuesDialog = (isNew: boolean): React.ReactNode => {
    return <IssueDlg isNew={isNew} />;
  }

  public static closeDialog = () => {
    if (this.isDialogOpen()) {
      ModelessDialogManager.closeDialog("issueDialog");
    }
  }
}
